import api from "@/lib/axios"

import type {
    MockModule,
    MockQuestion,
    MockTest,
    MockTestResult,
} from "@/services/mock-tests"

/**
 * Runtime client for sitting an in-house mock test.
 *
 * Implements the same method signatures as `mockTestService` so the existing
 * `TestInterface` can be pointed at it without a second copy of that component.
 * Access is by the free-mock access key rather than a login, so the key rides
 * along on every call.
 */

const BASE = "/inhouse-mock"

export interface InhouseMockTestState extends MockTest {
    current_stage: "RW_1" | "RW_2" | "BREAK" | "MATH_1" | "MATH_2" | "DONE"
    seconds_remaining: number
    candidate_name: string
}

/** Matches the shape TestInterface consumes from `mockTestService`. */
export interface MockRuntimeService {
    getCurrentModule(testId: number): Promise<MockModule>
    submitModule(
        testId: number,
        moduleId: number,
        answers: { question_id: number; user_answer: string; time_taken_seconds?: number }[]
    ): Promise<MockTest>
    saveAnswer(
        testId: number,
        moduleId: number,
        questionId: number,
        userAnswer: string
    ): Promise<void>
    markQuestionForReview(
        testId: number,
        moduleId: number,
        questionId: number,
        isMarked: boolean
    ): Promise<void>
    pauseTest(testId: number, timeRemaining: number): Promise<MockTest>
    getQuestion(testId: number, questionId: number): Promise<MockQuestion>
    reportQuestion(
        mockQuestionId: number,
        issueTopic: string,
        description?: string
    ): Promise<{ status: string; message: string }>
    updateMockQuestion(
        testId: number,
        questionId: string | number,
        data: Partial<MockQuestion>
    ): Promise<MockQuestion>
}

/** Built per sitting because every call needs the access key. */
export function createInhouseRuntimeService(accessKey: string): MockRuntimeService {
    const key = { access_key: accessKey }

    return {
        async getCurrentModule(sessionId) {
            const res = await api.get<MockModule>(`${BASE}/sessions/${sessionId}/module`, {
                params: key,
            })
            return res.data
        },

        async submitModule(sessionId) {
            // Answers are already persisted one at a time, so submitting only
            // closes the stage — the payload the shared interface sends is not
            // needed here and is deliberately ignored.
            const res = await api.post<MockTest>(
                `${BASE}/sessions/${sessionId}/submit-module`,
                null,
                { params: key }
            )
            return res.data
        },

        async saveAnswer(sessionId, _moduleId, questionId, userAnswer) {
            await api.post(
                `${BASE}/sessions/${sessionId}/answer`,
                { question_id: questionId, answer: userAnswer },
                { params: key }
            )
        },

        async markQuestionForReview(sessionId, _moduleId, questionId, isMarked) {
            await api.post(
                `${BASE}/sessions/${sessionId}/answer`,
                { question_id: questionId, flagged: isMarked },
                { params: key }
            )
        },

        // Pausing is not offered on a supervised sitting; the timer is derived
        // from the server's stage start, so there is nothing to pause.
        async pauseTest(sessionId) {
            const res = await api.get<MockTest>(`${BASE}/sessions/${sessionId}`, { params: key })
            return res.data
        },

        // Reviewer-only affordances in the shared interface. They are gated on a
        // signed-in role, which these sitters never have, so they are inert
        // rather than wired to endpoints that do not exist here.
        async getQuestion() {
            throw new Error("Not available while sitting an in-house test")
        },
        async reportQuestion() {
            return { status: "unavailable", message: "Reporting is not available here." }
        },
        async updateMockQuestion() {
            throw new Error("Not available while sitting an in-house test")
        },
    }
}

export const inhouseMockRuntime = {
    /** Always begins a fresh sitting — any earlier attempt is abandoned. */
    async start(accessKey: string, testId: number): Promise<InhouseMockTestState> {
        const res = await api.post<InhouseMockTestState>(`${BASE}/sessions`, {
            access_key: accessKey,
            test_id: testId,
        })
        return res.data
    },

    async getState(sessionId: number, accessKey: string): Promise<InhouseMockTestState> {
        const res = await api.get<InhouseMockTestState>(`${BASE}/sessions/${sessionId}`, {
            params: { access_key: accessKey },
        })
        return res.data
    },

    /** The finished sitting, in the same shape as the logged-in runtime's report. */
    async getResult(sessionId: number, accessKey: string): Promise<MockTestResult> {
        const res = await api.get<MockTestResult>(`${BASE}/sessions/${sessionId}/result`, {
            params: { access_key: accessKey },
        })
        return res.data
    },

    async advance(sessionId: number, accessKey: string): Promise<InhouseMockTestState> {
        const res = await api.post<InhouseMockTestState>(
            `${BASE}/sessions/${sessionId}/submit-module`,
            null,
            { params: { access_key: accessKey } }
        )
        return res.data
    },
}
